import { create } from "zustand";
import * as _ from "lodash";
import { PollTypesEnum } from "@shared/Types/Polls";
import { generateUuid } from "@infrastructure/Services/UuidGenerator";
import { PollResponseOption } from "../../Post.types";

export type PollOptionType = {
    content: PollResponseOption["content"] | string;
    id: PollResponseOption["id"];
    isCorrectAnswer: boolean;
    isDeleted: boolean;
    persistedId: number | undefined;
};

export type PollOptionId = PollOptionType["id"];

type ClosedAtType = number | undefined;

export const CLOSED_AT_OPTIONS: { [key: string]: ClosedAtType } = {
    offen: undefined,
    "6 h": 6,
    "24 h": 24,
    "3 Tage": 24 * 3,
    "7 Tage": 24 * 7,
};

interface PollOptionsStateType {
    addPollOption: (value: PollOptionType) => void;
    canAddNewOption: (pollOptions: PollOptionType[]) => boolean;
    changeAnswer: (answerId: PollOptionId | undefined) => void;
    clearPollOptions: () => void;
    closedAt: ClosedAtType;
    getFilteredPollOptions: () => PollOptionType[];
    pollOptions: PollOptionType[];
    pollType: PollTypesEnum;
    removePollOption: (id: PollOptionId) => void;
    resetPollOptions: (pollOptions: PollOptionType[]) => void;
    setClosedAt: (value: ClosedAtType) => void;
    updatePollOption: (id: PollOptionId, value: string) => void;
    updatePollType: (value: PollTypesEnum) => void;
}

function generateEmptyOption() {
    return {
        id: generateUuid(),
        content: "",
        isDeleted: false,
        persistedId: undefined,
        isCorrectAnswer: false,
    };
}

export const DEFAULT_EMPTY_POLL_OPTIONS = [
    generateEmptyOption(),
    generateEmptyOption(),
];
export const POLL_OPTIONS_LIMIT = 10;

export const usePollOptionsState = create<PollOptionsStateType>()(
    (set, get) => ({
        pollType: PollTypesEnum.POLL_OFF,
        pollOptions: DEFAULT_EMPTY_POLL_OPTIONS,
        canAddNewOption: (arrayOfPollOptions) => {
            const filteredPollOptions = get().getFilteredPollOptions();

            return (
                filteredPollOptions.length < POLL_OPTIONS_LIMIT &&
                filteredPollOptions.length >= 0 &&
                arrayOfPollOptions.every(({ content: input }) => input !== "")
            );
        },
        addPollOption: (newValue) =>
            set(() => {
                const filteredPollOptions = get().getFilteredPollOptions();
                const newPollOptions = [
                    ...filteredPollOptions,
                    {
                        ...newValue,
                        id: generateUuid(),
                    },
                ];
                if (get().canAddNewOption(newPollOptions)) {
                    newPollOptions.push(generateEmptyOption());
                }
                return {
                    pollOptions: newPollOptions,
                };
            }),
        updatePollOption: (key, newValue) => {
            const tempPollOptions = _.cloneDeep(get().pollOptions);
            const index = tempPollOptions.findIndex(({ id }) => id === key);
            tempPollOptions[index].content = newValue;
            if (get().canAddNewOption(tempPollOptions)) {
                tempPollOptions.push(generateEmptyOption());
            }
            set(() => ({
                pollOptions: [...tempPollOptions],
            }));
        },
        removePollOption: (key) => {
            const tempPollOptions = get().pollOptions.filter(
                ({ id }) => id !== key,
            );
            if (get().canAddNewOption(tempPollOptions)) {
                tempPollOptions.push(generateEmptyOption());
            }
            set(() => ({
                pollOptions: [...tempPollOptions],
            }));
        },
        resetPollOptions: (pollOptions: PollOptionType[]) =>
            set(() => ({
                pollOptions,
            })),
        clearPollOptions: () =>
            set(() => ({
                pollOptions: DEFAULT_EMPTY_POLL_OPTIONS,
            })),
        getFilteredPollOptions: () =>
            get().pollOptions.filter(({ content: value }) => value.length > 0),
        closedAt: Object.values(CLOSED_AT_OPTIONS)[0],
        setClosedAt: (expireAt) =>
            set({
                closedAt: expireAt,
            }),
        updatePollType: (value) => {
            get().changeAnswer(undefined);
            set({
                pollType: value,
                pollOptions:
                    value === PollTypesEnum.POLL_RATING
                        ? DEFAULT_EMPTY_POLL_OPTIONS
                        : get().pollOptions,
            });
        },
        changeAnswer: (answerId) => {
            set({
                pollOptions: get().pollOptions.map((option) => ({
                    ...option,
                    isCorrectAnswer: option.id === answerId,
                })),
            });
        },
    }),
);
